'use client'

import { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import type { Post } from '@/lib/api'
import type { Locale } from '@/lib/i18n'
import VideoCard from './VideoCard'
import VideoFilters from './VideoFilters'

type Category = {
  slug: string
  name_fr: string
  name_es: string
  show_in_nav: boolean
  order: number
}

interface VideoPageClientProps {
  initialVideos: Post[]
  locale: Locale
  categories?: Category[]
}

export default function VideoPageClient({ 
  initialVideos, 
  locale,
  categories = []
}: VideoPageClientProps) {
  const [filteredVideos, setFilteredVideos] = useState<Post[]>(initialVideos)
  const [activeCategory, setActiveCategory] = useState('all')
  const [activeSort, setActiveSort] = useState('recent')
  const [videosToShow, setVideosToShow] = useState(12)

  const tui = useTranslations('ui')

  // Filter and sort videos
  useEffect(() => {
    let filtered = [...initialVideos]

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter(video => video.category === activeCategory)
    }

    // Sort videos
    switch (activeSort) {
      case 'popular':
        filtered = filtered.sort((a, b) => (b.view_count || 0) - (a.view_count || 0))
        break
      case 'duration':
        // Mock sorting by duration for now
        filtered = filtered.sort(() => Math.random() - 0.5)
        break
      case 'recent':
      default:
        filtered = filtered.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
        break
    }

    setFilteredVideos(filtered)
    // Reset videos to show when filters change
    setVideosToShow(12)
  }, [initialVideos, activeCategory, activeSort])

  return (
    <>
      {/* Page Title */}
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-wider lg:text-4xl">
          Vidéos
        </h1>
        <div className="h-0.5 flex-1 ml-6 bg-gradient-to-r from-gray-300 to-transparent" />
      </div>

      {/* Filters */}
      <VideoFilters
        onCategoryChange={setActiveCategory}
        onSortChange={setActiveSort}
        activeCategory={activeCategory}
        activeSort={activeSort}
        categories={categories}
      />

      {/* Filtered Videos Grid */}
      {filteredVideos.length > 0 && (
        <section className="mb-16">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {filteredVideos.slice(0, videosToShow).map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          {/* Load More Button */}
          {filteredVideos.length > videosToShow && (
            <div className="mt-12 text-center">
              <button 
                onClick={() => setVideosToShow(prev => prev + 12)}
                className="rounded-full bg-blue-600 px-8 py-3 text-white font-semibold transition-all duration-300 hover:bg-blue-700 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-500/50"
              >
                {tui('seeMoreVideos')}
              </button>
            </div>
          )}
        </section>
      )}


    </>
  )
}